import { Box, Button, Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridValueGetterParams } from '@mui/x-data-grid-pro';
import React from 'react';

import { If } from 'app/components/if';
import { RequestStatus } from 'app/components/request-status';
import { SeamStatus } from 'app/constants/seam-status';
import { Store } from 'app/model';
import { Brand } from 'app/model/cris/brand';
import { CrisRequest } from 'app/model/cris-request';
import { ModelWithoutId } from 'app/type';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

interface CrisExecutionProps {
    request: CrisRequest;

    onExecute: () => void;
}

interface CrisExecutionState {
    anchorEl?: Element;
    lastBrand?: Brand;
}

const columns: Array<GridColDef<ModelWithoutId<Store>>> = [
    {
        field: 'id',
        headerName: 'Store Id',
        width: 120,
        sortable: false,
        valueGetter: (params: GridValueGetterParams<ModelWithoutId<Store>, ModelWithoutId<Store>['id']>) =>
            params.value ? params.value : '(new)',
    },
    {
        field: 'brand',
        headerName: 'Brand',
        width: 150,
        sortable: false,
        valueGetter: (params: GridValueGetterParams<ModelWithoutId<Store>, ModelWithoutId<Store>['brand']>) =>
            params.value ? params.value.name : '(keep)',
    },
    {
        field: 'name',
        headerName: 'Name',
        width: 200,
        sortable: false,
    },
    {
        field: 'storeNum',
        headerName: 'Store Num',
        width: 120,
        sortable: false,
    },
    {
        field: 'locationId',
        headerName: 'Loc ID',
        width: 120,
        sortable: false,
    },
    {
        field: 'phone',
        headerName: 'Phone',
        width: 120,
        sortable: false,
    },
    {
        field: 'type',
        headerName: 'Type',
        width: 120,
        sortable: false,
    },
    {
        field: 'isDefault',
        headerName: 'Default',
        width: 100,
        sortable: false,
        renderCell: (params) => (params.value ? <strong>Y</strong> : 'N'),
    },
    {
        field: 'domain',
        headerName: 'Domain',
        width: 150,
        sortable: false,
    },
    {
        field: 'storeownerId',
        headerName: 'Owner Id',
        width: 100,
        sortable: false,
    },
    {
        field: 'midGiven',
        headerName: 'MID Given',
        width: 150,
        sortable: false,
    },
    {
        field: 'dateOpened',
        headerName: 'Date Opened',
        width: 150,
        sortable: false,
        valueFormatter: DataGridFormatters.dateFormatter,
    },
    {
        field: 'dateClosed',
        headerName: 'Date Closed',
        width: 150,
        sortable: false,
        valueFormatter: DataGridFormatters.dateFormatter,
    },
    {
        field: 'source',
        headerName: 'Source',
        width: 150,
        sortable: false,
    },
    {
        field: 'defaultChannel',
        headerName: 'Default Channel',
        width: 150,
        sortable: false,
    },
];

export class CrisExecution extends React.Component<CrisExecutionProps, CrisExecutionState> {
    public constructor(props: CrisExecutionProps | Readonly<CrisExecutionProps>) {
        super(props);

        this.state = {};
    }

    public render(): React.ReactNode {
        const createValue = this.props.request.brand?.id ? 'updated' : 'created';
        const isReview = SeamStatus.TXN_CHECK === this.props.request.currentPlace;
        let i = 0;

        return (
            <Box display='flex' flexDirection='column'>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Card elevation={4} className='mb-2' style={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant='h4'>CRIS Request</Typography>
                                <Typography variant='h6'>
                                    Status <RequestStatus status={this.props.request.currentPlace} />
                                </Typography>
                                <Typography variant='h6'>Description</Typography>
                                {this.props.request.description}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6}>
                        <Card elevation={4} className='mb-2' style={{ height: '100%' }}>
                            <CardHeader title='Brand Details' />
                            <CardContent>
                                <Typography>
                                    The following brand will be <strong>{createValue}</strong>
                                </Typography>
                                <Box display='flex'>
                                    <Box mr={3}>
                                        <Typography variant='h6'>New Name</Typography>
                                        <Typography>{this.props.request.brandData?.name}</Typography>
                                        <Typography variant='h6'>New Category</Typography>
                                        <Typography>{this.props.request.brandData?.category?.category}</Typography>
                                    </Box>
                                    <Box mr={3}>
                                        <Typography variant='h6'>New Mid Count</Typography>
                                        <Typography>{this.props.request.brandData?.midCount}</Typography>
                                        <Typography variant='h6'>New Location</Typography>
                                        <Typography>{this.props.request.brandData?.locationId}</Typography>
                                    </Box>
                                    {this.props.request.brand?.id ? (
                                        <React.Fragment>
                                            <Box mr={3}>
                                                <Typography variant='h6'>Old Name</Typography>
                                                <Typography>{this.props.request.brand.name}</Typography>
                                                <Typography variant='h6'>Old Category</Typography>
                                                <Typography>{this.props.request.brand.category?.category}</Typography>
                                            </Box>
                                            <Box mr={3}>
                                                <Typography variant='h6'>Old Mid Count</Typography>
                                                <Typography>{this.props.request.brand.midCount}</Typography>
                                                <Typography variant='h6'>Old Location</Typography>
                                                <Typography>{this.props.request.brand.locationId}</Typography>
                                            </Box>
                                        </React.Fragment>
                                    ) : undefined}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                        <Card elevation={4} className='mb-2'>
                            <CardHeader title='Store Actions' />
                            <CardContent>
                                <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                                    <DataGridPro
                                        rows={this.props.request.storeData}
                                        columns={columns}
                                        paginationModel={{ pageSize: 5, page: 0 }}
                                        disableRowSelectionOnClick
                                        autoHeight
                                        disableColumnMenu
                                        getRowId={() => i++}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <If condition={isReview}>
                    <Box display='flex' justifyContent='flex-end'>
                        <Button variant='contained' size='small' color='primary' onClick={this.props.onExecute}>
                            Execute
                        </Button>
                    </Box>
                </If>
            </Box>
        );
    }
}
